// typescript
import React, {useEffect, useState} from "react";
import {Container} from "../../SensingSystemArchitectureDesign/common/Container";
import styled from "styled-components";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import {usePostRequest} from "../../../../api/helper";
import {ROAD_ANALYSIS_CONSTANTS} from "../constants";
import {toast} from "react-hot-toast";
import {roundToNDecimals} from "../../../../helpers/helpers";
import MaterialReactTable, {MRT_ColumnDef} from "material-react-table";
import {ArgosTableSimple} from "../../../components/Table/ArgosTableSimple";

/* styled components */
const QueryContainer = styled.div({
  width: "100%",
  height: "100%",
  display: "flex",
  gap: 16,
  padding: 16,
  background: "#fff",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  borderRadius: 8,
  boxSizing: "border-box",
  overflow: "auto",
});
const QueryItem = styled.div({
  flex: 1,
  border: "1px solid #ddd",
  padding: "16px",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  boxSizing: "border-box",
});
const ItemHeader = styled.div({
  textAlign: "center",
  fontWeight: 700,
  fontSize: 16,
  marginBottom: 12,
});
const SectionHeader = styled.div({
  fontWeight: 700,
  marginBottom: 12,
  marginTop: 8,
});
const InputsGrid = styled.div({
  display: "grid",
  gridTemplateColumns: "max-content 1fr",
  columnGap: 12,
  rowGap: 10,
  alignItems: "center",
  width: "100%",
});
const Label = styled.label({
  justifySelf: "start",
  paddingRight: 8,
  whiteSpace: "nowrap",
  textAlign: "left",
  color: "#222",
  display: "flex",
  alignItems: "center",
  gap: 8,
});

/* types */
type CurvFields = {
  segDistance: string;
  maxRadius: string;
  errors: Record<string, string | undefined>;
};

type ElevFields = {
  segDistance: string;
  percent: string;
  errors: Record<string, string | undefined>;
};

type CurvOutputs = Record<string, string>;
type ElevOutputs = Record<string, {elevation: string; grade: string; angle: string}>;

const ROAD_TYPES_CURV = ["residential", "primary", "secondary", "tertiary"];
const ROAD_TYPES_ELEV = [...ROAD_TYPES_CURV, "motorway", "trunk"];
const ROUND_OUTPUTS_TO = 3;

/**
 * Returns output value for text field: rounds to fixed decimals or returns empty string if no value.
 * @param val - The value to format.
 * @returns Rounded string or empty string.
 */
const getOutputValueForTextField = (val: string): string => val ? String(roundToNDecimals(Number(val), ROUND_OUTPUTS_TO)) : "";

const _unwrapBackendPayload = (res: any): { payload: any; errors?: Record<string, string> } => {
  const raw = res?.data ?? res ?? {};
  if (raw && typeof raw === "object") {
    if ("results" in raw && typeof raw.results === "object") {
      return { payload: raw.results, errors: raw.errors };
    }
    // direct mapping
    return { payload: raw, errors: raw.errors };
  }
  return { payload: {}, errors: undefined };
};

/**
 * RoadAnalysis component for querying and displaying road curvature and elevation analysis.
 * Handles input validation, API requests, and output rendering.
 */
const RoadAnalysis: React.FC = () => {
  const [leftInputs] = useState([
    {key: "segDistance", label: "Segment Distance (m)", unit: "m"},
    {key: "maxRadius", label: "Max Radius of Curvature (m)", unit: "m"},
  ]);

  const [rightInputs] = useState([
    {key: "segDistance", label: "Segment Distance (m)", unit: "m"},
    {key: "percent", label: "Percent of roads <= elevation (%)", unit: "%"},
  ]);

  const [curvState, setCurvState] = useState<CurvFields>({segDistance: "", maxRadius: "", errors: {}});
  const [elevState, setElevState] = useState<ElevFields>({segDistance: "", percent: "", errors: {}});

  const [curvOutputs, setCurvOutputs] = useState<CurvOutputs>({});
  const [elevOutputs, setElevOutputs] = useState<ElevOutputs>({});

  const [curvLoading, setCurvLoading] = useState(false);
  const [elevLoading, setElevLoading] = useState(false);
  const [curvError, setCurvError] = useState<string | null>(null);
  const [elevError, setElevError] = useState<string | null>(null);

  const postCurv = usePostRequest(ROAD_ANALYSIS_CONSTANTS.landing.endpoints.curv);
  const postElev = usePostRequest(ROAD_ANALYSIS_CONSTANTS.landing.endpoints.elev);

  /**
   * Handles input field changes for both queries.
   * @param side - "curv" or "elev"
   * @param field - Field name
   * @param value - New value
   */
  const handleFieldChange = (side: "curv" | "elev", field: string, value: string) => {
    if (side === "curv") {
      setCurvState((s) => ({...s, [field]: value, errors: {...s.errors, [field]: undefined}}));
    } else {
      setElevState((s) => ({...s, [field]: value, errors: {...s.errors, [field]: undefined}}));
    }
  };

  /**
   * Validates input fields for the given query side.
   * @param side - "curv" or "elev"
   * @returns Errors object
   */
  const validateSide = (side: "curv" | "elev") => {
    const state = side === "curv" ? curvState : elevState;
    const errors: Record<string, string | undefined> = {};

    /**
     * Checks if a value is a valid non-negative number.
     * @param val - Value to check
     * @param name - Field name
     */
    const checkNumber = (val: string, name: string) => {
      if (!val && val !== "0") {
        errors[name] = "Required";
        return;
      }
      const n = Number(val);
      if (Number.isNaN(n) || n < 0) {
        errors[name] = "Must be a non-negative number";
      }
    };

    checkNumber(state.segDistance, "segDistance");

    if (side === "elev") {
      const percent = (state as ElevFields).percent;
      if (!percent && percent !== "0") {
        errors.percent = "Required";
      } else {
        const p = Number(percent);
        if (Number.isNaN(p) || p < 0 || p > 100) {
          errors.percent = "Must be a number between 0 and 100";
        }
      }
    } else {
      checkNumber((state as CurvFields).maxRadius, "maxRadius");
    }

    if (side === "curv") {
      setCurvState((s) => ({...s, errors}));
    } else {
      setElevState((s) => ({...s, errors}));
    }

    return errors;
  };

  /**
   * Effect: validates curvature inputs and resets outputs/errors if invalid.
   */
  useEffect(() => {
    const errors = validateSide("curv");
    if (Object.keys(errors).length !== 0) {
      setCurvOutputs({});
      setCurvError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curvState.segDistance, curvState.maxRadius]);

  /**
   * Effect: validates elevation inputs and resets outputs/errors if invalid.
   */
  useEffect(() => {
    const errors = validateSide("elev");
    if (Object.keys(errors).length !== 0) {
      setElevOutputs({});
      setElevError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elevState.segDistance, elevState.percent]);

  /**
   * Handles curvature query generation.
   * Validates inputs, sends API request, and updates outputs.
   */
  const handleGenerateCurv = () => {
    const errors = validateSide("curv");
    if (Object.keys(errors).length !== 0) {
      toast.error("Fix input errors before generating.");
      return;
    }
    const seg = Number(curvState.segDistance);
    const maxR = Number(curvState.maxRadius);
    if (Number.isNaN(seg) || Number.isNaN(maxR)) {
      toast.error("Provide valid numeric inputs.");
      return;
    }

    setCurvLoading(true);
    setCurvError(null);
    // send an array of road types to backend
    postCurv({segDistance: seg, maxRadius: maxR, roadTypes: ROAD_TYPES_CURV})
        .then((res) => {
          const {payload, errors} = _unwrapBackendPayload(res);
          if (payload && typeof payload === "object") {
            setCurvOutputs(payload as CurvOutputs);
          } else {
            setCurvOutputs({});
          }
          // surface per-type errors if any
          if (errors && Object.keys(errors).length > 0) {
            setCurvError("Some road types failed (see logs).");
          } else {
            setCurvError(null);
          }
        })
        .catch((e) => {
          console.error("curv generate failed", e);
          setCurvOutputs({});
          setCurvError("Failed to generate curvature outputs.");
        })
        .finally(() => {
          setCurvLoading(false);
        });
  };

  /**
   * Handles elevation query generation.
   * Validates inputs, sends API request, and updates outputs.
   */
  const handleGenerateElev = () => {
  const errors = validateSide("elev");
  if (Object.keys(errors).length !== 0) {
    toast.error("Fix input errors before generating.");
    return;
  }
  const seg = Number(elevState.segDistance);
  const pct = Number(elevState.percent);
  if (Number.isNaN(seg) || Number.isNaN(pct)) {
    toast.error("Provide valid numeric inputs.");
    return;
  }

  setElevLoading(true);
  setElevError(null);
  // send an array of road types to backend
  postElev({ segDistance: seg, percent: pct, roadTypes: ROAD_TYPES_ELEV })
    .then((res) => {
      const { payload, errors } = _unwrapBackendPayload(res);
      if (payload && typeof payload === "object") {
        setElevOutputs(payload as ElevOutputs);
      } else {
        setElevOutputs({});
      }
      if (errors && Object.keys(errors).length > 0) {
        setElevError("Some road types failed (see logs).");
      } else {
        setElevError(null);
      }
    })
    .catch((e) => {
      console.error("elev generate failed", e);
      setElevOutputs({});
      setElevError("Failed to generate elevation outputs.");
    })
    .finally(() => {
      setElevLoading(false);
    });
};

  /* Material React Table columns and data mapping */
  type CurvRow = { roadType: string; percent: string };
  type ElevRow = { roadType: string; elevation: string; grade: string; angle: string };

  const curvColumns: MRT_ColumnDef<CurvRow>[] = [
    { accessorKey: "roadType", header: "Road Type", muiTableBodyCellProps: {style: {textTransform: "capitalize"}} },
    {
      accessorKey: "percent",
      header: "Percent of Roads (%) with curvature >= Max Radius of Curvature",
      muiTableHeadCellProps: {align: "right"},
      muiTableBodyCellProps: {align: "right"},
      size: 500
    },
  ];

  const elevColumns: MRT_ColumnDef<ElevRow>[] = [
    { accessorKey: "roadType", header: "Road Type", muiTableBodyCellProps: {style: {textTransform: "capitalize"}} },
    {
      accessorKey: "elevation",
      header: "Elevation (m)",
      muiTableHeadCellProps: {align: "right"},
      muiTableBodyCellProps: {align: "right"},
    },
    {
      accessorKey: "grade",
      header: "Grade",
      muiTableHeadCellProps: {align: "right"},
      muiTableBodyCellProps: {align: "right"},
    },
    {
      accessorKey: "angle",
      header: "Angle (deg)",
      muiTableHeadCellProps: {align: "right"},
      muiTableBodyCellProps: {align: "right"},
    },
  ];

  const curvData: CurvRow[] = ROAD_TYPES_CURV.map((rt) => ({
    roadType: rt,
    percent: getOutputValueForTextField(curvOutputs[rt] ?? ""),
  }));

  const elevData: ElevRow[] = ROAD_TYPES_ELEV.map((rt) => {
    const out = elevOutputs[rt] ?? {elevation: "", grade: "", angle: ""};
    return {
      roadType: rt,
      elevation: getOutputValueForTextField(out.elevation),
      grade: getOutputValueForTextField(out.grade),
      angle: getOutputValueForTextField(out.angle),
    };
  });

  return (
    <Container style={{justifyContent: "center", alignItems: "center"}}>
      <QueryContainer>
        <QueryItem>
          <ItemHeader>Curvature Query</ItemHeader>

          <SectionHeader>Inputs:</SectionHeader>
          <InputsGrid>
            {leftInputs.map((f) => (
              <React.Fragment key={f.key}>
                <Label htmlFor={`curv-${f.key}`}>{f.label}</Label>
                <div>
                  <TextField
                    id={`curv-${f.key}`}
                    type="number"
                    variant="standard"
                    value={(curvState as any)[f.key] ?? ""}
                    onChange={(e) => handleFieldChange("curv", f.key, e.target.value)}
                    onBlur={() => validateSide("curv")}
                    InputProps={{endAdornment: <InputAdornment position="end">{f.unit}</InputAdornment>}}
                    error={!!curvState.errors[f.key]}
                    helperText={curvState.errors[f.key]}
                    fullWidth
                  />
                </div>
              </React.Fragment>
            ))}
          </InputsGrid>

          <div style={{marginTop: 12}}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleGenerateCurv}
              disabled={curvLoading}
              startIcon={curvLoading ? <CircularProgress size={16} color="inherit"/> : undefined}
            >
              {curvLoading ? "Generating..." : "Generate"}
            </Button>
          </div>

          <SectionHeader style={{marginTop: 20}}>Outputs</SectionHeader>
          <div style={{width: "100%"}}>
            <ArgosTableSimple
              columns={curvColumns}
              data={curvData}
              enableTopToolbar={false}
              enableBottomToolbar={false}
              enablePagination={false}
              initialState={{density: "compact"}}
              muiTablePaperProps={{variant: "outlined", style: {overflowX: "auto"}}}
              renderTopToolbarCustomActions={() => null}
              muiTableHeadCellProps={{sx: {fontWeight: 700}}}
              state={{isLoading: curvLoading}}
            />
          </div>

          {curvError ? <div style={{marginTop: 8}}><Alert severity="error">{curvError}</Alert></div> : null}
        </QueryItem>

        <QueryItem>
          <ItemHeader>Elevation Query</ItemHeader>

          <SectionHeader>Inputs:</SectionHeader>
          <InputsGrid>
            {rightInputs.map((f) => (
              <React.Fragment key={f.key}>
                <Label htmlFor={`elev-${f.key}`}>{f.label}</Label>
                <div>
                  <TextField
                    id={`elev-${f.key}`}
                    type="number"
                    variant="standard"
                    value={(elevState as any)[f.key] ?? ""}
                    onChange={(e) => handleFieldChange("elev", f.key, e.target.value)}
                    onBlur={() => validateSide("elev")}
                    InputProps={{endAdornment: <InputAdornment position="end">{f.unit}</InputAdornment>}}
                    error={!!elevState.errors[f.key]}
                    helperText={elevState.errors[f.key]}
                    fullWidth
                  />
                </div>
              </React.Fragment>
            ))}
          </InputsGrid>

          <div style={{marginTop: 12}}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleGenerateElev}
              disabled={elevLoading}
              startIcon={elevLoading ? <CircularProgress size={16} color="inherit"/> : undefined}
            >
              {elevLoading ? "Generating..." : "Generate"}
            </Button>
          </div>

          <SectionHeader style={{marginTop: 20}}>Outputs</SectionHeader>
          <div style={{width: "100%"}}>
            <ArgosTableSimple
              columns={elevColumns}
              data={elevData}
              enableTopToolbar={false}
              enableBottomToolbar={false}
              enablePagination={false}
              initialState={{density: "compact"}}
              muiTablePaperProps={{variant: "outlined", style: {overflowX: "auto"}}}
              renderTopToolbarCustomActions={() => null}
              muiTableHeadCellProps={{sx: {fontWeight: 700}}}
              state={{isLoading: elevLoading}}
            />
          </div>

          {elevError ? <div style={{marginTop: 8}}><Alert severity="error">{elevError}</Alert></div> : null}
        </QueryItem>
      </QueryContainer>
    </Container>
  );
};

export default RoadAnalysis;
